import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axios';
import { DashboardLayout } from '../common/page_component/index.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import DocumentModal from '../common/DocumentModal';
import Alert from '../common/Alert';
import ShipmentTrackingPanel from '../tracking/ShipmentTrackingPanel';
import { shipmentService } from '../../services/shipmentService';
import { ArrowLeft, Package, Search, Eye, Download } from 'lucide-react';

const UserShipmentTracking = () => {
  const navigate = useNavigate();
  const [shipmentId, setShipmentId] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returnId, setReturnId] = useState(null);
  const [loadingReturnId, setLoadingReturnId] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [deliveryImageModal, setDeliveryImageModal] = useState(false);
  const [selectedDeliveryImage, setSelectedDeliveryImage] = useState(null);

  useEffect(() => {
    if (trackingData?.shipment_details?.status?.toLowerCase() === 'returned') {
      fetchReturnId(trackingData.shipment_details.shipment_id);
    }
  }, [trackingData]);

  const fetchReturnId = async (id) => {
    try {
      setLoadingReturnId(true);
      const response = await axios.get('/shipments/returned', {
        params: { return_status: 'close' },
      });

      if (response.data.success && response.data.data?.length > 0) {
        const returns = response.data.data.filter((r) => r.shipment_id === id);
        if (returns.length > 0) {
          const sortedReturns = returns.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setReturnId(sortedReturns[0].return_id);
        }
      }
    } catch (err) {
      console.error('Error fetching return ID:', err);
      setReturnId(null);
    } finally {
      setLoadingReturnId(false);
    }
  };

  const handleViewDeliveryImage = (imageUrl) => {
    setSelectedDeliveryImage(imageUrl);
    setDeliveryImageModal(true);
  };

  const handleTrack = async (e) => {
    e.preventDefault();

    if (!shipmentId.trim()) {
      setError('Please enter a consignment number or GC number');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);
    setReturnId(null);

    try {
      const response = await axios.get(`/tracking/${shipmentId}`);
      if (response.data.success) {
        setTrackingData(response.data);
      } else {
        setError(response.data.error || 'Shipment not found');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tracking information');
    } finally {
      setLoading(false);
    }
  };

  const handleViewShipment = () => {
    if (trackingData?.shipment_details?.shipment_id) {
      navigate(`/shipments/view/${trackingData.shipment_details.shipment_id}`);
    }
  };

  const handleDownloadInvoice = async () => {
    const id = trackingData?.shipment_details?.shipment_id;
    if (!id) return;
    try {
      setDownloadingInvoice(true);
      const response = await shipmentService.downloadInvoice(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Invoice downloaded successfully');
    } catch (err) {
      console.error('Error downloading invoice:', err);
      Alert.error('Failed to download invoice');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleViewReturn = () => {
    if (returnId) {
      navigate(`/returns/view/${returnId}`);
    }
  };

  const isReturnedStatus = trackingData?.shipment_details?.status?.toLowerCase() === 'returned';

  const headerActions = trackingData ? (
    <>
      <Button variant="primary" icon={Eye} onClick={handleViewShipment} className="w-full sm:w-auto">
        View Shipment
      </Button>
      <Button
        variant="secondary"
        icon={Download}
        onClick={handleDownloadInvoice}
        loading={downloadingInvoice}
        disabled={downloadingInvoice}
        className="w-full sm:w-auto"
      >
        Download Invoice
      </Button>
      {isReturnedStatus && returnId && (
        <Button
          variant="danger"
          icon={Eye}
          onClick={handleViewReturn}
          loading={loadingReturnId}
          disabled={loadingReturnId}
          className="w-full sm:w-auto"
        >
          View Return
        </Button>
      )}
    </>
  ) : null;

  return (
    <DashboardLayout fullWidth>
      <div className="min-w-0 space-y-2 sm:space-y-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-700 flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <Package className="w-5 h-5 sm:w-6 sm:h-6 text-buttonhoverTxt shrink-0" />
            <span className="truncate">Track Shipment</span>
          </h1>
          <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5 pl-10 sm:pl-11">
            Enter consignment number or GC number to get real-time tracking updates
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-2.5 sm:p-4 md:p-5 min-w-0">
          <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <input
              type="text"
              value={shipmentId}
              onChange={(e) => setShipmentId(e.target.value)}
              placeholder="Consignment No or GC Number"
              className="flex-1 min-w-0 px-3 py-2 sm:py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 transition-all"
            />
            <Button
              type="submit"
              variant="primary"
              icon={Search}
              loading={loading}
              disabled={loading}
              className="w-full sm:w-auto sm:min-w-[110px] shrink-0"
            >
              {loading ? 'Tracking...' : 'Track'}
            </Button>
          </form>
        </div>

        {loading && (
          <div className="bg-white rounded-xl shadow-md p-4">
            <Loader fullScreen={false} size="default" message="Tracking your shipment..." />
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4">
            <p className="text-red-800 font-medium text-sm sm:text-base break-words">{error}</p>
          </div>
        )}

        {!loading && trackingData && (
          <ShipmentTrackingPanel
            trackingData={trackingData}
            onViewDeliveryImage={handleViewDeliveryImage}
            headerActions={headerActions}
          />
        )}

        {!trackingData && !loading && !error && (
          <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 text-center min-w-0">
            <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 text-sm sm:text-base font-medium">
              Enter a consignment number or GC number above to view tracking information
            </p>
          </div>
        )}
      </div>

      <DocumentModal
        isOpen={deliveryImageModal}
        onClose={() => setDeliveryImageModal(false)}
        documentUrl={selectedDeliveryImage || ''}
        documentType="Government ID proof"
      />
    </DashboardLayout>
  );
};

export default UserShipmentTracking;
